import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorReportContext } from '@imagise/entities';
import { ErrorReportFormComponent } from '../error-report-form/error-report-form.component';

/**
 * Lightweight, self-contained modal (no Angular Material / CDK) used to prompt
 * for a report after a non-fatal error. Hosts the {@link ErrorReportFormComponent}
 * and closes on backdrop click or Escape.
 */
@Component({
  selector: 'lib-error-report-overlay',
  standalone: true,
  imports: [ErrorReportFormComponent, TranslateModule],
  templateUrl: './error-report-overlay.component.html',
  styleUrl: './error-report-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorReportOverlayComponent {
  /** Incident to report. */
  readonly context = input<ErrorReportContext | null>(null);

  /** Emitted when the user dismisses the overlay. */
  readonly closed = output<void>();

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly reportForm = viewChild(ErrorReportFormComponent);
  readonly title = computed(() => {
    switch (this.reportForm()?.status()) {
      case 'queued': return 'ERROR_REPORTING.FORM.QUEUED_TITLE';
      case 'success': return 'ERROR_REPORTING.FORM.SUCCESS_TITLE';
      case 'reported': return 'ERROR_REPORTING.FORM.ALREADY_REPORTED_TITLE';
      default: return this.context()?.kind === 'feedback' ? 'ERROR_REPORTING.FEEDBACK.TITLE' : 'ERROR_REPORTING.TITLE';
    }
  });

  constructor() {
    // Move focus into the dialog on open: improves a11y and lets Escape
    // (bound on the backdrop) catch the key from anywhere inside via bubbling.
    afterNextRender(() => this.panel().nativeElement.focus());
  }

  trapFocus(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const panel = this.panel().nativeElement;
    const controls = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href]'
      )
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = panel.ownerDocument.activeElement;
    const outsideControls = !controls.some(control => control === active);
    if (event.shiftKey && (active === first || outsideControls)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || outsideControls)) {
      event.preventDefault();
      first?.focus();
    }
  }

  close(): void {
    this.closed.emit();
  }

  /** Closes only when the backdrop itself is clicked, not its content. */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
